import { useField } from 'formik';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { TooltipPosition } from 'components/controls/Tooltip';
import { ErrorMessage } from 'styles/Styles.styled';
import { useTheme } from 'styled-components';
import { Theme } from 'styles/theme';

type Props = {
  name: string;
  label?: string;
  tooltip?: string;
  tooltipPosition?: TooltipPosition;
  required?: boolean;
  supressErrors?: boolean;

  shouldShowBottomError?: (error: string) => boolean;
  shouldHighlightBorder?: (error: string) => boolean;
  renderErrorMessage?: (error: string) => ReactNode;
  errorMessageStyle?: React.CSSProperties;

  [key: string]: any;
};

const TextAreaInput = ({
  label,
  tooltip,
  tooltipPosition,
  supressErrors,
  shouldShowBottomError,
  shouldHighlightBorder,
  renderErrorMessage,
  errorMessageStyle,
  ...props
}: Props) => {
  const theme = useTheme() as Theme;
  const [field, meta] = useField(props);

  const rawError = meta.touched ? (meta.error as string | undefined) : undefined;

  const shouldHighlight = shouldHighlightBorder ? (rawError ? shouldHighlightBorder(rawError) : false) : false;
  const isBottomAllowed = shouldShowBottomError ? (rawError ? shouldShowBottomError(rawError) : false) : true;

  const borderColor = shouldHighlight ? theme.colors.systemStatusError : undefined;

  const errorNode = rawError && renderErrorMessage ? renderErrorMessage(rawError) : rawError;

  return (
    <>
      {label && (
        <SmartLabel
          htmlFor={props.id || props.name}
          label={label}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          required={props.required}
          hasValue={field.value}
        />
      )}

      <FixedTextArea {...field} {...props} $borderColor={borderColor} />

      {meta.touched && meta.error && !supressErrors && isBottomAllowed ? (
        <ErrorMessage style={errorMessageStyle}>{errorNode}</ErrorMessage>
      ) : null}
    </>
  );
};

const FixedTextArea = styled.textarea<{ $borderColor?: string }>`
  padding: 1rem;
  border: solid 1px ${(p) => p.$borderColor ?? 'gray'};
  margin: 0.5rem 0 1rem 0;
  min-width: 100%;
  ${(props) => props.theme.fonts.textSmall}

  ::placeholder {
    color: ${(props) => props.theme.colors.primaryBlack70};
    ${(props) => props.theme.fonts.textSmall}
  }
`;

export default TextAreaInput;
